<template>
  <!-- Botão de recolher/expandir o menu lateral (fora do header para sobrepor o sidebar) -->
  <button
    type="button"
    class="sidebar-toggle-btn"
    :class="{ collapsed: stateStore.sidebarCollapsed && !stateStore.sidebarHoverExpanded }"
    @click="stateStore.toggleSidebarCollapsed()"
    :title="stateStore.sidebarCollapsed ? 'Expandir menu' : 'Recolher menu'"
  >
    <i class="fas fa-chevron-left"></i>
  </button>

  <!-- Header Desktop -->
  <div id="main-header" class="desktop-header">
    <!-- Seletor de região (e banca para ADMIN) centralizado -->
    <div class="header-center">
      <RegionSelect />
    </div>

    <!-- Dropdown do usuário no lado direito -->
    <div class="user-dropdown" ref="userDropdown">
      <div class="user-info" @click.stop="toggleDropdown">
        <div class="user-avatar">
          <i class="fas fa-user"></i>
        </div>
        <span class="username">{{ userName || 'Usuário' }}</span>
        <span v-if="userRole !== null && userRole !== undefined" :class="['user-role-badge', getRoleBadgeClass()]">
          {{ getRoleText() }}
        </span>
        <i class="fas fa-chevron-down dropdown-arrow" :class="{ 'rotated': showDropdown }"></i>
      </div>
      
      <!-- Dropdown menu -->
      <div v-if="showDropdown" class="dropdown-menu" @click.stop>
        <router-link to="/account" class="dropdown-item" @click="closeDropdown">
          <i class="fas fa-key"></i>
          Trocar Senha
        </router-link>
        <div class="dropdown-item" @click="logout()">
          <i class="fas fa-sign-out-alt"></i>
          Sair do Sistema
        </div>
      </div>
    </div>
  </div>

  <!-- Header Mobile -->
  <div id="mobile-header" class="mobile-header">
    <!-- Menu Hamburger -->
    <div class="mobile-menu-toggle" @click="toggleMobileMenu">
      <i class="fas fa-bars"></i>
      <span class="menu-text">Menu</span>
    </div>

    <!-- Logo/Título centralizado -->
    <div class="mobile-title">
      <div v-if="settings && getCurrentLogo()" class="mobile-logo">
        <img :src="getCurrentLogo()" :alt="settings.siteName || 'Logo'" class="mobile-logo-image">
      </div>
      <span v-else>{{ settings?.siteName }}</span>
    </div>

    <!-- Usuário mobile -->
    <div class="mobile-user" @click.stop="toggleDropdown">
      <div class="mobile-user-avatar">
        <i class="fas fa-user"></i>
      </div>
    </div>

    <!-- Dropdown menu mobile -->
    <div v-if="showDropdown" class="mobile-dropdown-menu" @click.stop>
      <div class="mobile-user-info">
        <span class="mobile-username">{{ userName || 'Usuário' }}</span>
        <span v-if="userRole !== null && userRole !== undefined" :class="['user-role-badge', getRoleBadgeClass()]">
          {{ getRoleText() }}
        </span>
      </div>
      <router-link to="/account" class="mobile-dropdown-item" @click="closeDropdown">
        <i class="fas fa-key"></i>
        Trocar Senha
      </router-link>
      <div class="mobile-dropdown-item" @click="logout()">
        <i class="fas fa-sign-out-alt"></i>
        Sair do Sistema
      </div>
    </div>
  </div>

  <!-- Menu Mobile Overlay -->
  <div v-if="showMobileMenu" class="mobile-menu-overlay" @click="closeMobileMenu">
    <div class="mobile-menu-content" @click.stop>
      <div class="mobile-menu-header">
        <span>Menu Principal</span>
        <button class="mobile-menu-close" @click="closeMobileMenu">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <nav class="mobile-menu-nav">
        <!-- Dashboard - não visível para AGENTE -->
        <router-link v-if="!isAgente" to="/dashboard" class="mobile-menu-item" @click="closeMobileMenu">
          <i class="fas fa-chart-pie"></i>
          <span>Dashboard</span>
        </router-link>

        <!-- Bancas (com submenu) - ADMIN -->
        <div v-if="hasPermission('bancas')" class="mobile-menu-group">
          <div class="mobile-menu-item mobile-menu-parent" @click="toggleMobileSubmenu('bancas')">
            <div class="mobile-menu-item-content">
              <i class="fas fa-building"></i>
              <span>Bancas</span>
            </div>
            <i class="fas fa-chevron-right" :class="{ 'rotated': showMobileSubmenus.bancas }"></i>
          </div>
          <div v-if="showMobileSubmenus.bancas" class="mobile-submenu">
            <router-link to="/bancas" class="mobile-submenu-item" @click="closeMobileMenu">
              <i class="fas fa-list"></i>
              <span>Listagem</span>
            </router-link>
            <router-link to="/regions" class="mobile-submenu-item" @click="closeMobileMenu">
              <i class="fas fa-earth-americas"></i>
              <span>Regiões</span>
            </router-link>
          </div>
        </div>

        <!-- Regiões - ADMIN, GERENTE e BANCA (quando não há permissão para bancas) -->
        <router-link 
          v-if="hasPermission('regions') && !hasPermission('bancas')" 
          to="/regions" 
          class="mobile-menu-item" 
          @click="closeMobileMenu"
        >
          <i class="fas fa-earth-americas"></i>
          <span>Regiões</span>
        </router-link>

        <!-- Loterias - ADMIN, GERENTE e BANCA -->
        <router-link v-if="hasPermission('lotteries')" to="/lotteries" class="mobile-menu-item" @click="closeMobileMenu">
          <i class="fas fa-clock"></i>
          <span>Loterias</span>
        </router-link>

        <!-- Cobradores (com submenu) -->
        <div v-if="hasPermission('collectors')" class="mobile-menu-group">
          <div class="mobile-menu-item mobile-menu-parent" @click="toggleMobileSubmenu('collectors')">
            <div class="mobile-menu-item-content">
              <i class="fas fa-user-check"></i>
              <span>Cobradores</span>
            </div>
            <i class="fas fa-chevron-right" :class="{ 'rotated': showMobileSubmenus.collectors }"></i>
          </div>
          <div v-if="showMobileSubmenus.collectors" class="mobile-submenu">
            <router-link to="/collectors" class="mobile-submenu-item" @click="closeMobileMenu">
              <i class="fas fa-list"></i>
              <span>Listagem</span>
            </router-link>
            <router-link to="/collector-commissions" class="mobile-submenu-item" @click="closeMobileMenu">
              <i class="fas fa-chart-line"></i>
              <span>Comissões</span>
            </router-link>
            <router-link to="/collector-history" class="mobile-submenu-item" @click="closeMobileMenu">
              <i class="fas fa-history"></i>
              <span>Histórico</span>
            </router-link>
            <router-link to="/collector-charges" class="mobile-submenu-item" @click="closeMobileMenu">
              <i class="fas fa-hand-holding-usd"></i>
              <span>Cobranças</span>
            </router-link>
          </div>
        </div>

        <!-- Vendedores (com submenu) -->
        <div v-if="hasPermission('sellers')" class="mobile-menu-group">
          <div class="mobile-menu-item mobile-menu-parent" @click="toggleMobileSubmenu('sellers')">
            <div class="mobile-menu-item-content">
              <i class="fas fa-user"></i>
              <span>Vendedores</span>
            </div>
            <i class="fas fa-chevron-right" :class="{ 'rotated': showMobileSubmenus.sellers }"></i>
          </div>
          <div v-if="showMobileSubmenus.sellers" class="mobile-submenu">
            <router-link to="/sellers" class="mobile-submenu-item" @click="closeMobileMenu">
              <i class="fas fa-user-plus"></i>
              <span>Cadastro</span>
            </router-link>
            <router-link to="/sellers/bets" class="mobile-submenu-item" @click="closeMobileMenu">
              <i class="fas fa-ticket"></i>
              <span>Bilhetes</span>
            </router-link>
            <router-link to="/sellers/entries" class="mobile-submenu-item" @click="closeMobileMenu">
              <i class="fas fa-calculator"></i>
              <span>Fechamento</span>
            </router-link>
            <router-link to="/sellers/lottery-history" class="mobile-submenu-item" @click="closeMobileMenu">
              <i class="fas fa-history"></i>
              <span>Histórico por Loterias</span>
            </router-link>
          </div>
        </div>

        <!-- Lançamentos -->
        <router-link v-if="hasPermission('financialtransactions')" to="/financialTransactions" class="mobile-menu-item" @click="closeMobileMenu">
          <i class="fas fa-dollar-sign"></i>
          <span>Lançamentos</span>
        </router-link>

        <!-- Fechamento -->
        <router-link v-if="hasPermission('entries')" to="/entries" class="mobile-menu-item" @click="closeMobileMenu">
          <i class="fas fa-circle-dollar-to-slot"></i>
          <span>Fechamento</span>
        </router-link>

        <!-- Resultados - ADMIN, GERENTE, BANCA e AGENTE -->
        <router-link v-if="hasPermission('results')" to="/results" class="mobile-menu-item" @click="closeMobileMenu">
          <i class="fas fa-grip"></i>
          <span>Resultados</span>
        </router-link>

        <!-- Descargas (com submenu) - ADMIN, GERENTE e BANCA -->
        <div v-if="hasPermission('descargas')" class="mobile-menu-group">
          <div class="mobile-menu-item mobile-menu-parent" @click="toggleMobileSubmenu('descargas')">
            <div class="mobile-menu-item-content">
              <i class="fas fa-download"></i>
              <span>Descargas</span>
            </div>
            <i class="fas fa-chevron-right" :class="{ 'rotated': showMobileSubmenus.descargas }"></i>
          </div>
          <div v-if="showMobileSubmenus.descargas" class="mobile-submenu">
            <!-- Descarga Enviada: NÃO mostrar para Redescarga (OnlyDescarga=true E DescargaRegionId=null) -->
            <router-link 
              v-if="shouldShowDescargaEnviada" 
              to="/descargas-enviada" 
              class="mobile-submenu-item" 
              @click="closeMobileMenu"
            >
              <i class="fas fa-paper-plane"></i>
              <span>Descarga Enviada</span>
            </router-link>
            <!-- Descarga Recebida: NÃO mostrar para Normal (OnlyDescarga=false) -->
            <router-link 
              v-if="shouldShowDescargaRecebida" 
              to="/descargas-recebida" 
              class="mobile-submenu-item" 
              @click="closeMobileMenu"
            >
              <i class="fas fa-inbox"></i>
              <span>Descarga Recebida</span>
            </router-link>
            <router-link to="/historico-descarga" class="mobile-submenu-item" @click="closeMobileMenu">
              <i class="fas fa-history"></i>
              <span>Histórico de Descarga</span>
            </router-link>
          </div>
        </div>

        <!-- Usuários - ADMIN, GERENTE e BANCA -->
        <router-link v-if="hasPermission('users')" to="/users" class="mobile-menu-item" @click="closeMobileMenu">
          <i class="fas fa-users"></i>
          <span>Usuários</span>
        </router-link>

        <!-- Configurações da Região - ADMIN, GERENTE, BANCA e COBRADOR -->
        <router-link v-if="hasPermission('regionsconfigs')" to="/regionsconfigs" class="mobile-menu-item" @click="closeMobileMenu">
          <i class="fas fa-sliders"></i>
          <span>Configurações da Região</span>
        </router-link>

        <!-- Aplicativos - disponível para todos os usuários autenticados -->
        <router-link to="/aplicativos" class="mobile-menu-item" @click="closeMobileMenu">
          <i class="fas fa-mobile-screen-button"></i>
          <span>Baixar App</span>
        </router-link>

        <!-- Sistema (com submenu) - só ADMIN -->
        <div v-if="hasPermission('settings')" class="mobile-menu-group">
          <div class="mobile-menu-item mobile-menu-parent" @click="toggleMobileSubmenu('system')">
            <div class="mobile-menu-item-content">
              <i class="fas fa-gear"></i>
              <span>Sistema</span>
            </div>
            <i class="fas fa-chevron-right" :class="{ 'rotated': showMobileSubmenus.system }"></i>
          </div>
          <div v-if="showMobileSubmenus.system" class="mobile-submenu">
            <router-link to="/tasks" class="mobile-submenu-item" @click="closeMobileMenu">
              <i class="fas fa-list-check"></i>
              <span>Tarefas</span>
            </router-link>
            <router-link to="/app-versions" class="mobile-submenu-item" @click="closeMobileMenu">
              <i class="fas fa-mobile-alt"></i>
              <span>Versões do App</span>
            </router-link>
            <router-link to="/settings" class="mobile-submenu-item" @click="closeMobileMenu">
              <i class="fas fa-cog"></i>
              <span>Configurações</span>
            </router-link>
            <router-link to="/api-clients" class="mobile-submenu-item" @click="closeMobileMenu">
              <i class="fas fa-key"></i>
              <span>Clientes API</span>
            </router-link>
            <div 
              class="mobile-submenu-item mobile-submenu-action" 
              @click="handleClearCache"
              :class="{ 'loading': clearingCache }"
            >
              <i class="fas fa-trash-can"></i>
              <span>{{ clearingCache ? 'Limpando...' : 'Limpar cache' }}</span>
            </div>
            <router-link to="/whatsapp" class="mobile-submenu-item" @click="closeMobileMenu">
              <i class="fas fa-bell"></i>
              <span>Notificações</span>
            </router-link>
            <router-link to="/whatsconfig" class="mobile-submenu-item" @click="closeMobileMenu">
              <i class="fas fa-mobile-screen-button"></i>
              <span>Instâncias WhatsApp</span>
            </router-link>
          </div>
        </div>
      </nav>
    </div>
  </div>

  <!-- Bottom Navigation Mobile -->
  <div id="mobile-bottom-nav" class="mobile-bottom-nav">
    <router-link v-if="hasPermission('sellers')" to="/sellers/entries" class="bottom-nav-item" :class="{ active: $route.path === '/sellers/entries' }">
      <i class="fas fa-user"></i>
      <span>Vendedores</span>
    </router-link>
    
    <router-link v-if="hasPermission('financialtransactions')" to="/financialTransactions" class="bottom-nav-item" :class="{ active: $route.path === '/financialTransactions' }">
      <i class="fas fa-dollar-sign"></i>
      <span>Lançamentos</span>
    </router-link>
    
    <!-- Botão Central - Região -->
    <div class="region-button-wrapper">
      <!-- Dropdown de Regiões -->
      <transition name="slide-up">
        <div v-if="showRegionDropdown" class="region-dropdown" @click.stop>
          <div class="region-dropdown-header">
            <span>{{ isAdmin && !selectedBancaInDropdown ? 'Selecione a Banca' : 'Selecione a Região' }}</span>
            <button class="region-dropdown-close" @click="closeRegionDropdown">
              <i class="fas fa-times"></i>
            </button>
          </div>
          
          <div class="region-dropdown-content">
            <!-- ADMIN: Seleção de Banca -->
            <div v-if="isAdmin && !selectedBancaInDropdown">
              <!-- Loading Bancas -->
              <div v-if="loadingBancas" class="region-dropdown-loading">
                <i class="fas fa-spinner fa-spin"></i>
                <span>Carregando bancas...</span>
              </div>
              
              <!-- Sem bancas -->
              <div v-else-if="!availableBancas || availableBancas.length === 0" class="region-dropdown-empty">
                <i class="fas fa-exclamation-circle"></i>
                <span>Nenhuma banca disponível</span>
              </div>
              
              <!-- Lista de bancas -->
              <div 
                v-else
                v-for="banca in availableBancas" 
                :key="banca.id" 
                class="region-dropdown-item"
                @click="selectBancaInDropdown(banca)"
              >
                <i class="fas fa-building"></i>
                <span>{{ banca.name }}</span>
                <i class="fas fa-chevron-right"></i>
              </div>
            </div>
            
            <!-- ADMIN: Regiões da Banca Selecionada OU Não-Admin: Regiões -->
            <div v-else>
              <!-- Breadcrumb para ADMIN -->
              <div v-if="isAdmin && selectedBancaInDropdown" class="region-dropdown-breadcrumb">
                <button @click="clearBancaSelection" class="breadcrumb-back">
                  <i class="fas fa-arrow-left"></i>
                  <span>Voltar</span>
                </button>
                <div class="breadcrumb-current">
                  <i class="fas fa-building"></i>
                  <span>{{ selectedBancaInDropdown.name }}</span>
                </div>
              </div>
              
              <!-- Loading Regiões -->
              <div v-if="loadingRegions" class="region-dropdown-loading">
                <i class="fas fa-spinner fa-spin"></i>
                <span>Carregando regiões...</span>
              </div>
              
              <!-- Sem regiões -->
              <div v-else-if="!availableRegions || availableRegions.length === 0" class="region-dropdown-empty">
                <i class="fas fa-exclamation-circle"></i>
                <span>Nenhuma região disponível</span>
              </div>
              
              <!-- Lista de regiões -->
              <div 
                v-else
                v-for="region in availableRegions" 
                :key="region.id" 
                class="region-dropdown-item"
                :class="{ 'active': currentRegion && currentRegion.id === region.id }"
                @click="selectRegion(region)"
              >
                <i class="fas fa-map-marker-alt"></i>
                <span>{{ region.name }}</span>
                <i v-if="currentRegion && currentRegion.id === region.id" class="fas fa-check region-check"></i>
              </div>
            </div>
          </div>
        </div>
      </transition>
      
      <!-- Botão Central Circular -->
      <button 
        class="region-central-button" 
        @click.stop="toggleRegionDropdown"
        :class="{ 'active': showRegionDropdown }"
      >
        <div class="region-button-circle">
          <i class="fas fa-map-marker-alt"></i>
        </div>
        <span class="region-button-label">{{ currentRegionName }}</span>
      </button>
    </div>
    
    <router-link v-if="hasPermission('entries')" to="/entries" class="bottom-nav-item" :class="{ active: $route.path === '/entries' }">
      <i class="fas fa-chart-line"></i>
      <span>Fechamento</span>
    </router-link>
    
    <router-link v-if="hasPermission('collectors')" to="/collectors" class="bottom-nav-item" :class="{ active: $route.path === '/collectors' }">
      <i class="fas fa-user-check"></i>
      <span>Cobradores</span>
    </router-link>
  </div>
</template>

<script>
import RegionSelect from "./../RegionSelect.vue";
import { hasPermission } from '@/services/permissions';
import mainService from "@/services/mainService";
import { toast } from "@/composables/useToast";
import { handleErrorMessage } from "@/services/config";
import stateStore from "@/utils/store";

export default {
  components: {
    RegionSelect,
  },
  data() {
    return {
      stateStore,
      clearingCache: false,
      showDropdown: false,
      showMobileMenu: false,
      showMobileSubmenus: {
        sellers: false,
        collectors: false,
        bancas: false,
        descargas: false,
        system: false
      },
      settings: null,
      showRegionDropdown: false,
      availableRegions: [],
      currentRegion: null,
      loadingRegions: false,
      availableBancas: [],
      selectedBancaInDropdown: null,
      loadingBancas: false,
      allRegions: [], // Todas as regiões para verificação
    };
  },
  computed: {
    user() {
      return JSON.parse(localStorage.getItem('user') || '{}');
    },
    userName() {
      return this.user.name || null;
    },
    userRole() {
      return this.user.role;
    },
    currentRegionName() {
      if (!this.currentRegion) return 'Região';
      return this.currentRegion.name || 'Região';
    },
    isAdmin() {
      return this.user.role === 0;
    },
    isAgente() {
      return this.user.role === 4;
    },
    isBanca() {
      return this.user.role === 3;
    },
    // Determinar se deve mostrar "Descarga Enviada"
    // NÃO mostrar para Redescarga (OnlyDescarga=true E DescargaRegionId=null)
    shouldShowDescargaEnviada() {
      // Admin sempre vê
      if (this.isAdmin) {
        return true;
      }
      
      // Para BANCA: verificar se tem apenas regiões de redescarga
      if (this.isBanca) {
        // Se não há regiões carregadas, mostrar por padrão
        if (!this.allRegions || this.allRegions.length === 0) {
          return true;
        }
        
        // Verificar se todas as regiões são redescarga (OnlyDescarga=true E DescargaRegionId=null)
        const hasOnlyRedescargaRegions = this.allRegions.every(region => 
          region.onlyDescarga === true && !region.descargaRegionId
        );
        
        // Se tem apenas redescarga, NÃO mostrar (redescarga não envia, só recebe)
        if (hasOnlyRedescargaRegions) {
          return false;
        }
        
        return true;
      }
      
      // Para outros usuários, verificar a região atual
      if (!this.currentRegion) return true;
      
      // Se é Redescarga (OnlyDescarga=true E DescargaRegionId=null), NÃO mostrar
      if (this.currentRegion.onlyDescarga && !this.currentRegion.descargaRegionId) {
        return false;
      }
      
      return true;
    },
    // Determinar se deve mostrar "Descarga Recebida"
    // NÃO mostrar para Normal (OnlyDescarga=false)
    shouldShowDescargaRecebida() {
      // Admin sempre vê
      if (this.isAdmin) {
        return true;
      }
      
      // Para BANCA: verificar se tem pelo menos uma região de descarga (OnlyDescarga=true)
      if (this.isBanca) {
        // Se não há regiões carregadas, mostrar por padrão
        if (!this.allRegions || this.allRegions.length === 0) {
          return true;
        }
        
        // Verificar se a banca tem pelo menos uma região de descarga
        const hasDescargaRegion = this.allRegions.some(region => region.onlyDescarga === true);
        
        // Se não tem nenhuma região de descarga, NÃO mostrar (banca só tem região normal, só envia)
        return hasDescargaRegion;
      }
      
      // Para outros usuários, verificar a região atual
      if (!this.currentRegion) return true;
      
      // Se é Normal (OnlyDescarga=false), NÃO mostrar
      if (!this.currentRegion.onlyDescarga) {
        return false;
      }
      
      return true;
    }
  },
  async mounted() {
    // Fechar dropdown quando clicar fora
    document.addEventListener('click', this.handleClickOutside);
    
    // Aguardar um pouco para garantir que o localStorage foi carregado
    await new Promise(resolve => setTimeout(resolve, 100));
    
    await this.loadSettings();
    
    // Verificar se há usuário antes de carregar regiões
    if (this.user && this.user.id) {
      if (this.isAdmin) {
        await this.loadBancas();
      }
      await this.loadRegions();
    } else {
      // Tentar novamente após um delay
      setTimeout(async () => {
        if (this.user && this.user.id) {
          if (this.isAdmin) {
            await this.loadBancas();
          }
          await this.loadRegions();
        }
      }, 500);
    }
    
    // Escutar mudanças de região
    window.addEventListener('regionChanged', this.handleRegionChange);
    window.addEventListener('storage', this.handleStorageChange);
    window.addEventListener('bancaChanged', this.handleBancaChange);
    window.addEventListener('headerSelectsNeedRefresh', this.handleHeaderSelectsRefresh);
    
    // Observar mudanças de tema para atualizar logo
    this.themeObserver = new MutationObserver(() => {
      this.$forceUpdate(); // Force update to recalculate getCurrentLogo()
    });
    
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
    window.removeEventListener('regionChanged', this.handleRegionChange);
    window.removeEventListener('storage', this.handleStorageChange);
    window.removeEventListener('bancaChanged', this.handleBancaChange);
    window.removeEventListener('headerSelectsNeedRefresh', this.handleHeaderSelectsRefresh);
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
  },
  methods: {
    toggleDropdown() {
      this.showDropdown = !this.showDropdown;
    },
    
    closeDropdown() {
      this.showDropdown = false;
    },
    
    toggleMobileMenu() {
      this.showMobileMenu = !this.showMobileMenu;
      if (this.showMobileMenu) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    },

    async handleClearCache() {
      if (this.clearingCache) return;
      try {
        const confirmed = await this.$confirm({
          title: 'Limpar cache',
          message: 'Deseja limpar todo o cache do sistema? Os dados serão recarregados nas próximas requisições.'
        });
        if (!confirmed) return;
        this.clearingCache = true;
        const res = await mainService.clearAllCache();
        const msg = res?.data?.message || 'Cache limpo com sucesso!';
        toast.success(msg);
        this.closeMobileMenu();
      } catch (err) {
        if (err !== false) {
          toast.error(handleErrorMessage(err));
        }
      } finally {
        this.clearingCache = false;
      }
    },

    closeMobileMenu() {
      this.showMobileMenu = false;
      document.body.style.overflow = '';
      // Resetar submenus
      this.showMobileSubmenus = {
        sellers: false,
        collectors: false,
        bancas: false,
        descargas: false,
        system: false
      };
    },

    toggleMobileSubmenu(submenu) {
      this.showMobileSubmenus[submenu] = !this.showMobileSubmenus[submenu];
    },
    
    handleClickOutside(event) {
      if (this.$refs.userDropdown && !this.$refs.userDropdown.contains(event.target)) {
        this.showDropdown = false;
      }
    },
    
    logout() {
      this.showDropdown = false;
      localStorage.setItem("isAuthenticated", "false");
      localStorage.removeItem("user");
      localStorage.removeItem("currentRegion");
      localStorage.removeItem("selectedBanca");
      location.reload();
    },
    
    getRoleText() {
      switch (this.userRole) {
        case 0: return 'ADMIN';
        case 1: return 'GERENTE';
        case 2: return 'COBRADOR';
        case 3: return 'BANCA';
        case 4: return 'AGENTE';
        default: return '';
      }
    },
    
    getRoleBadgeClass() {
      switch (this.userRole) {
        case 0: return 'badge-admin';
        case 1: return 'badge-manager';
        case 2: return 'badge-collector';
        case 3: return 'badge-manager';
        case 4: return 'badge-collector';
        default: return 'badge-default';
      }
    },

    hasPermission(menuKey) {
      return hasPermission(this.userRole, menuKey);
    },

    async loadSettings() {
      try {
        const response = await mainService.getActiveSettings();
        if (response.data) {
          this.settings = response.data;
        }
      } catch (error) {
        this.settings = null;
      }
    },

    getCurrentLogo() {
      if (!this.settings) return null;
      
      const isDarkTheme = document.documentElement.hasAttribute('data-theme');
      
      if (isDarkTheme) {
        return this.settings.logoDark || this.settings.logoLight;
      } else {
        return this.settings.logoLight || this.settings.logoDark;
      }
    },

    async loadBancas() {
      try {
        this.loadingBancas = true;

        const response = await mainService.searchBancas({ isActive: true });
        this.availableBancas = response.data || [];

        // Verificar se há banca selecionada no localStorage
        const savedBanca = localStorage.getItem('selectedBanca');
        if (savedBanca) {
          try {
            const banca = JSON.parse(savedBanca);
            const foundBanca = this.availableBancas.find(b => b.id === banca.id);
            if (foundBanca) {
              this.selectedBancaInDropdown = foundBanca;
            }
          } catch (e) {
            // Erro ao parsear banca
          }
        }
      } catch (error) {
        this.availableBancas = [];
      } finally {
        this.loadingBancas = false;
      }
    },

    async loadRegions() {
      try {
        this.loadingRegions = true;

        // Carregar região atual do localStorage
        const savedRegion = localStorage.getItem('currentRegion');
        if (savedRegion) {
          try {
            this.currentRegion = JSON.parse(savedRegion);
          } catch (e) {
            this.currentRegion = null;
          }
        }

        // Buscar regiões usando a mesma lógica do RegionSelect
        let userBancaId = null;

        if (this.isAdmin) {
          // Para ADMIN: buscar regiões da BANCA selecionada (no dropdown ou localStorage)
          let banca = this.selectedBancaInDropdown;

          if (!banca) {
            const selectedBanca = localStorage.getItem('selectedBanca');

            if (selectedBanca) {
              try {
                banca = JSON.parse(selectedBanca);
              } catch (e) {
                // Erro ao parsear banca
              }
            }
          }

          if (banca) {
            try {
              const allRegionsResponse = await mainService.searchRegions();
              const allRegions = allRegionsResponse.data || [];
              this.availableRegions = allRegions.filter(region => region.bancaId === banca.id);
              
              // Para ADMIN, allRegions deve ter todas as regiões da banca selecionada
              this.allRegions = this.availableRegions;
            } catch (e) {
              this.availableRegions = [];
              this.allRegions = [];
            }
          } else {
            this.availableRegions = [];
            this.allRegions = [];
          }
        } else if (this.user.role === 3) {
          // Para BANCA: buscar regiões da própria banca
          try {
            const response = await mainService.getUserBancaId(this.user.id);
            userBancaId = response.data;

            const allRegionsResponse = await mainService.searchRegions();
            const allRegions = allRegionsResponse.data || [];
            this.availableRegions = allRegions.filter(region => region.bancaId === userBancaId);
            
            // Atualizar allRegions com apenas as regiões da banca para verificação do menu
            this.allRegions = this.availableRegions;
          } catch (error) {
            this.availableRegions = [];
            this.allRegions = [];
          }
        } else {
          // Para GERENTE/COBRADOR: buscar regiões do usuário
          try {
            const [userRegionsResponse, allRegionsResponse] = await Promise.all([
              mainService.getUserRegions(this.user.id),
              mainService.searchRegions()
            ]);

            const userRegions = userRegionsResponse.data || [];
            const allRegions = allRegionsResponse.data || [];

            const regionIds = userRegions.map(r => r.id);
            if (this.user.regionId && !regionIds.includes(this.user.regionId)) {
              regionIds.push(this.user.regionId);
            }

            this.availableRegions = allRegions.filter(region => regionIds.includes(region.id));
            
            // Atualizar allRegions com apenas as regiões do usuário para verificação do menu
            this.allRegions = this.availableRegions;
          } catch (error) {
            this.availableRegions = [];
            this.allRegions = [];
          }
        }
      } catch (error) {
        this.availableRegions = [];
      } finally {
        this.loadingRegions = false;
      }
    },

    toggleRegionDropdown() {
      this.showRegionDropdown = !this.showRegionDropdown;
      
      if (this.showRegionDropdown) {
        // Fechar dropdown ao clicar fora
        setTimeout(() => {
          document.addEventListener('click', this.closeRegionDropdown);
        }, 0);
      }
    },

    closeRegionDropdown() {
      this.showRegionDropdown = false;
      document.removeEventListener('click', this.closeRegionDropdown);
      
      // Resetar seleção de banca ao fechar (para admin sempre começar pela lista de bancas)
      // Comentado para manter a navegação mais fluida
      // if (this.isAdmin) {
      //   this.selectedBancaInDropdown = null;
      // }
    },

    selectRegion(region) {
      console.log('📍 Região selecionada:', region);
      this.currentRegion = region;
      localStorage.setItem('currentRegion', JSON.stringify(region));
      
      // Se for ADMIN e tiver selecionado uma banca no dropdown, salvar também
      if (this.isAdmin && this.selectedBancaInDropdown) {
        const currentSavedBanca = localStorage.getItem('selectedBanca');
        let needsUpdate = true;
        
        if (currentSavedBanca) {
          try {
            const savedBanca = JSON.parse(currentSavedBanca);
            if (savedBanca.id === this.selectedBancaInDropdown.id) {
              needsUpdate = false;
            }
          } catch (e) {
            // Ignore
          }
        }
        
        if (needsUpdate) {
          console.log('💾 Salvando banca selecionada:', this.selectedBancaInDropdown);
          localStorage.setItem('selectedBanca', JSON.stringify(this.selectedBancaInDropdown));
          
          // Disparar evento de mudança de banca
          window.dispatchEvent(new CustomEvent('bancaChanged', {
            detail: { banca: this.selectedBancaInDropdown }
          }));
        }
      }
      
      // Disparar eventos
      window.dispatchEvent(new CustomEvent('regionChanged', {
        detail: { region: region }
      }));
      
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'currentRegion',
        newValue: JSON.stringify(region),
        oldValue: null,
        storageArea: localStorage
      }));
      
      this.closeRegionDropdown();
      
      // Recarregar página após pequeno delay
      setTimeout(() => {
        location.reload();
      }, 100);
    },

    handleRegionChange(event) {
      if (event.detail && event.detail.region) {
        this.currentRegion = event.detail.region;
      }
    },

    handleStorageChange(event) {
      if (event.key === 'currentRegion') {
        try {
          this.currentRegion = event.newValue ? JSON.parse(event.newValue) : null;
        } catch (e) {
          this.currentRegion = null;
        }
      }
    },

    async handleBancaChange(event) {
      // Quando a banca mudar (para ADMIN), recarregar as regiões
      console.log('Banca changed, reloading regions...');
      if (this.isAdmin) {
        await this.loadBancas();
      }
      await this.loadRegions();
    },

    async handleHeaderSelectsRefresh(event) {
      const detail = event?.detail || {};
      const refreshBancas = detail.bancas !== false;
      const refreshRegions = detail.regions !== false;

      if (refreshBancas && this.isAdmin) {
        await this.loadBancas();
      }
      if (refreshRegions) {
        await this.loadRegions();
      }
    },

    async selectBancaInDropdown(banca) {
      console.log('🏢 Banca selecionada no dropdown:', banca);
      this.selectedBancaInDropdown = banca;

      // Persistir imediatamente para outras telas (ex.: criar região) usarem o vínculo
      if (banca) {
        localStorage.setItem('selectedBanca', JSON.stringify(banca));
        window.dispatchEvent(new CustomEvent('bancaChanged', {
          detail: { banca }
        }));
      }
      
      // Carregar regiões dessa banca
      await this.loadRegions();
    },

    clearBancaSelection() {
      console.log('🗑️ Limpando seleção de banca no dropdown');
      this.selectedBancaInDropdown = null;
      this.availableRegions = [];
      localStorage.removeItem('selectedBanca');
      window.dispatchEvent(new CustomEvent('bancaChanged', {
        detail: { banca: null }
      }));
    }
  },
};
</script>

<style scoped>
/* Header Desktop */
.desktop-header {
  height: 61px;
  background-color: #e2e8f0;
  border-bottom: 2px solid #cbd5e1;
  width: 100%;
  transition: all 0.3s ease;
  z-index: 150;
  display: flex;
  align-items: center;
  padding: 0 20px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  justify-content: space-between;
}

.sidebar-toggle-btn {
  position: fixed;
  top: 16px;
  left: 260px;
  transform: translateX(-50%);
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #1e293b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  z-index: 250;
  transition: left 0.3s ease, background-color 0.3s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.sidebar-toggle-btn:hover {
  background: #f1f5f9;
}

.sidebar-toggle-btn.collapsed {
  left: 70px;
}

.sidebar-toggle-btn i {
  font-size: 12px;
  transition: transform 0.3s ease;
}

.sidebar-toggle-btn.collapsed i {
  transform: rotate(180deg);
}

[data-theme="dark"] .sidebar-toggle-btn {
  background: #252529;
  border-color: #52525b;
  color: #f4f4f5;
}

[data-theme="dark"] .sidebar-toggle-btn:hover {
  background: #16161a;
}

@media (max-width: 768px) {
  .sidebar-toggle-btn {
    display: none;
  }
}

/* Mostrar apenas em desktop */
@media (min-width: 769px) {
  .mobile-header,
  .mobile-bottom-nav {
    display: none !important;
  }
}

/* Esconder desktop em mobile */
@media (max-width: 768px) {
  .desktop-header {
    display: none !important;
  }
}

/* Tema escuro - Desktop */
[data-theme="dark"] .desktop-header {
  background-color: #252529;
  border-bottom-color: #52525b;
}

.header-center {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  max-width: 60%;
  white-space: nowrap;
}

.user-dropdown {
  position: relative;
  display: flex;
  align-items: center;
  z-index: 1000;
  margin-left: auto;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #1e293b;
  transition: all 0.3s ease;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
}

.user-info:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

/* Tema escuro */
[data-theme="dark"] .user-info {
  color: #f4f4f5;
}

[data-theme="dark"] .user-info:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.username {
  font-weight: 500;
  font-size: 14px;
}

.dropdown-arrow {
  font-size: 12px;
  margin-left: 4px;
  transition: transform 0.3s ease;
}

.dropdown-arrow.rotated {
  transform: rotate(180deg);
}

.user-role-badge {
  display: inline-block;
  padding: 0.2rem 0.5rem;
  border-radius: 0.75rem;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  vertical-align: middle;
}

.badge-admin {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
}

.badge-manager {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
}

.badge-collector {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
}

.badge-default {
  background-color: #6b7280;
  color: white;
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 5px);
  right: 0;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  min-width: 180px;
  z-index: 9999 !important;
  overflow: hidden;
  animation: slideDown 0.2s ease-out;
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Tema escuro para dropdown */
[data-theme="dark"] .dropdown-menu {
  background-color: #252529;
  border-color: #52525b;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  color: #374151;
  text-decoration: none;
  transition: all 0.2s ease;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
  white-space: nowrap;
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover {
  background-color: #f9fafb;
  color: #1f2937;
}

/* Tema escuro para dropdown items */
[data-theme="dark"] .dropdown-item {
  color: #d1d5db;
  border-bottom-color: #52525b;
}

[data-theme="dark"] .dropdown-item:hover {
  background-color: #16161a;
  color: #f9fafb;
}

.dropdown-item i {
  width: 16px;
  text-align: center;
  font-size: 14px;
}

/* ===== MOBILE STYLES ===== */

/* Header Mobile */
.mobile-header {
  display: none;
  height: 60px;
  background-color: #e2e8f0;
  border-bottom: 2px solid #cbd5e1;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  align-items: center;
  padding: 0 15px;
  justify-content: space-between;
}

@media (max-width: 768px) {
  .mobile-header {
    display: flex;
  }
}

[data-theme="dark"] .mobile-header {
  background-color: #252529;
  border-bottom-color: #52525b;
}

.mobile-menu-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: none;
  border: none;
  color: #1e293b;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.mobile-menu-toggle:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

[data-theme="dark"] .mobile-menu-toggle {
  color: #f4f4f5;
}

[data-theme="dark"] .mobile-menu-toggle:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.mobile-menu-toggle i {
  font-size: 18px;
}

.menu-text {
  font-size: 14px;
  font-weight: 500;
}

.mobile-title {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

[data-theme="dark"] .mobile-title {
  color: #f4f4f5;
}

.mobile-logo {
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-logo-image {
  max-width: 120px;
  max-height: 32px;
  object-fit: contain;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

.mobile-user {
  cursor: pointer;
  position: relative;
}

.mobile-user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Mobile Dropdown */
.mobile-dropdown-menu {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  z-index: 9999;
  overflow: hidden;
  animation: slideDown 0.2s ease-out;
}

[data-theme="dark"] .mobile-dropdown-menu {
  background-color: #252529;
  border-color: #52525b;
}

.mobile-user-info {
  padding: 16px;
  border-bottom: 1px solid #f3f4f6;
  text-align: center;
}

[data-theme="dark"] .mobile-user-info {
  border-bottom-color: #52525b;
}

.mobile-username {
  display: block;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
}

[data-theme="dark"] .mobile-username {
  color: #f4f4f5;
}

.mobile-dropdown-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  color: #374151;
  text-decoration: none;
  transition: all 0.2s ease;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
}

.mobile-dropdown-item:last-child {
  border-bottom: none;
}

.mobile-dropdown-item:hover {
  background-color: #f9fafb;
  color: #1f2937;
}

[data-theme="dark"] .mobile-dropdown-item {
  color: #d1d5db;
  border-bottom-color: #52525b;
}

[data-theme="dark"] .mobile-dropdown-item:hover {
  background-color: #16161a;
  color: #f9fafb;
}

/* Menu Mobile Overlay */
.mobile-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9998;
  display: flex;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.mobile-menu-content {
  background-color: white;
  width: 280px;
  height: 100%;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
  animation: slideInLeft 0.3s ease-out;
}

[data-theme="dark"] .mobile-menu-content {
  background-color: #252529;
}

@keyframes slideInLeft {
  from { transform: translateX(-100%); }
  to { transform: translateX(0); }
}

.mobile-menu-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #f8fafc, #e2e8f0);
}

[data-theme="dark"] .mobile-menu-header {
  border-bottom-color: #52525b;
  background: linear-gradient(135deg, #252529, #16161a);
}

.mobile-menu-header span {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
}

[data-theme="dark"] .mobile-menu-header span {
  color: #f4f4f5;
}

.mobile-menu-close {
  background: none;
  border: none;
  color: #6b7280;
  font-size: 18px;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.mobile-menu-close:hover {
  background-color: rgba(0, 0, 0, 0.05);
  color: #374151;
}

[data-theme="dark"] .mobile-menu-close {
  color: #9ca3af;
}

[data-theme="dark"] .mobile-menu-close:hover {
  background-color: rgba(255, 255, 255, 0.05);
  color: #d1d5db;
}

.mobile-menu-nav {
  padding: 10px 0;
}

.mobile-menu-item {
  display: flex;
  align-items: center;
  padding: 14px 20px;
  color: #374151;
  text-decoration: none;
  transition: all 0.2s ease;
  border-left: 3px solid transparent;
}

.mobile-menu-item:hover {
  background-color: #f9fafb;
  border-left-color: #3b82f6;
  color: #1e293b;
}

[data-theme="dark"] .mobile-menu-item {
  color: #d1d5db;
}

[data-theme="dark"] .mobile-menu-item:hover {
  background-color: #16161a;
  color: #f4f4f5;
}

.mobile-menu-item.router-link-active {
  background-color: #eff6ff;
  border-left-color: #3b82f6;
  color: #1e40af;
}

[data-theme="dark"] .mobile-menu-item.router-link-active {
  background-color: #1e3a8a;
  color: #93c5fd;
}

.mobile-menu-item i {
  width: 20px;
  margin-right: 12px;
  font-size: 16px;
  text-align: center;
}

.mobile-menu-parent {
  justify-content: space-between;
  cursor: pointer;
}

.mobile-menu-item-content {
  display: flex;
  align-items: center;
}

.mobile-menu-parent .fa-chevron-right {
  font-size: 12px;
  transition: transform 0.3s ease;
}

.mobile-menu-parent .fa-chevron-right.rotated {
  transform: rotate(90deg);
}

.mobile-submenu {
  background-color: #f8fafc;
  border-left: 3px solid #e2e8f0;
  animation: slideDown 0.2s ease-out;
}

[data-theme="dark"] .mobile-submenu {
  background-color: #16161a;
  border-left-color: #52525b;
}

.mobile-submenu-item {
  display: flex;
  align-items: center;
  padding: 12px 20px 12px 45px;
  color: #6b7280;
  text-decoration: none;
  transition: all 0.2s ease;
  font-size: 14px;
}

.mobile-submenu-item:hover {
  background-color: #f1f5f9;
  color: #374151;
}

[data-theme="dark"] .mobile-submenu-item {
  color: #9ca3af;
}

[data-theme="dark"] .mobile-submenu-item:hover {
  background-color: #0f0f23;
  color: #d1d5db;
}

.mobile-submenu-item.router-link-active {
  background-color: #dbeafe;
  color: #1e40af;
}

[data-theme="dark"] .mobile-submenu-item.router-link-active {
  background-color: #1e3a8a;
  color: #93c5fd;
}

.mobile-submenu-item i,
.mobile-submenu-item svg {
  width: 16px;
  margin-right: 8px;
  font-size: 14px;
  text-align: center;
  display: inline-block;
  vertical-align: middle;
  flex-shrink: 0;
}

/* Ícones brands (WhatsApp, etc) */
.mobile-submenu-item i.fa-brands,
.mobile-submenu-item i.fab {
  font-family: "Font Awesome 6 Brands", "Font Awesome 5 Brands", FontAwesome;
  font-weight: 400;
}

.mobile-submenu-action {
  cursor: pointer;
  border: none;
  background: none;
}

.mobile-submenu-action.loading {
  opacity: 0.7;
  pointer-events: none;
}

/* Bottom Navigation Mobile */
.mobile-bottom-nav {
  display: none;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: white;
  border-top: 1px solid #e5e7eb;
  padding: 8px 0;
  z-index: 999;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
}

@media (max-width: 768px) {
  .mobile-bottom-nav {
    display: flex;
  }
}

[data-theme="dark"] .mobile-bottom-nav {
  background-color: #252529;
  border-top-color: #52525b;
}

.bottom-nav-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 4px;
  color: #6b7280;
  text-decoration: none;
  font-size: 12px;
  transition: all 0.2s ease;
}

.bottom-nav-item:hover {
  color: #3b82f6;
}

.bottom-nav-item.active {
  color: #3b82f6;
}

[data-theme="dark"] .bottom-nav-item {
  color: #9ca3af;
}

[data-theme="dark"] .bottom-nav-item:hover,
[data-theme="dark"] .bottom-nav-item.active {
  color: #60a5fa;
}

.bottom-nav-item i {
  font-size: 18px;
  margin-bottom: 4px;
}

.bottom-nav-item span {
  font-weight: 500;
  line-height: 1;
}

/* Região - Botão Central */
.region-button-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.region-central-button {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-top: -20px;
  position: relative;
  z-index: 10;
}

.region-button-circle {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.7), 0 2px 4px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
}

[data-theme="dark"] .region-button-circle {
  border-color: #252529;
}

.region-button-circle i {
  font-size: 24px;
  color: white;
}

.region-central-button.active .region-button-circle,
.region-central-button:hover .region-button-circle {
  transform: translateY(-4px) scale(1.05);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.5), 0 4px 8px rgba(0, 0, 0, 0.15);
}

.region-central-button:active .region-button-circle {
  transform: translateY(-2px) scale(1.02);
}

.region-button-label {
  font-size: 11px;
  font-weight: 600;
  color: #3b82f6;
  margin-top: 4px;
  max-width: 70px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
}

[data-theme="dark"] .region-button-label {
  color: #60a5fa;
}

/* Dropdown de Regiões */
.region-dropdown {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 20px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.2), 0 -4px 12px rgba(0, 0, 0, 0.1);
  width: 300px;
  max-height: 400px;
  overflow: hidden;
}

[data-theme="dark"] .region-dropdown {
  background: #252529;
  box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.5), 0 -4px 12px rgba(0, 0, 0, 0.3);
}

.region-dropdown-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #f8fafc, #e2e8f0);
}

[data-theme="dark"] .region-dropdown-header {
  border-bottom-color: #52525b;
  background: linear-gradient(135deg, #252529, #16161a);
}

.region-dropdown-header span {
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
}

[data-theme="dark"] .region-dropdown-header span {
  color: #f4f4f5;
}

.region-dropdown-close {
  background: none;
  border: none;
  color: #6b7280;
  font-size: 18px;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.region-dropdown-close:hover {
  background-color: rgba(0, 0, 0, 0.05);
  color: #374151;
}

[data-theme="dark"] .region-dropdown-close {
  color: #9ca3af;
}

[data-theme="dark"] .region-dropdown-close:hover {
  background-color: rgba(255, 255, 255, 0.05);
  color: #d1d5db;
}

.region-dropdown-content {
  max-height: 350px;
  overflow-y: auto;
  padding: 8px;
}

.region-dropdown-breadcrumb {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: linear-gradient(135deg, #f8fafc, #e2e8f0);
  border-bottom: 1px solid #e5e7eb;
  margin: -8px -8px 8px -8px;
}

[data-theme="dark"] .region-dropdown-breadcrumb {
  background: linear-gradient(135deg, #1c1c1f, #16161a);
  border-bottom-color: #52525b;
}

.breadcrumb-back {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #3b82f6;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

[data-theme="dark"] .breadcrumb-back {
  background: #252529;
  border-color: #52525b;
  color: #60a5fa;
}

.breadcrumb-back:hover {
  background: #eff6ff;
  border-color: #3b82f6;
  transform: translateX(-2px);
}

[data-theme="dark"] .breadcrumb-back:hover {
  background: #1e3a8a;
  border-color: #60a5fa;
}

.breadcrumb-back i {
  font-size: 12px;
}

.breadcrumb-current {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
}

[data-theme="dark"] .breadcrumb-current {
  color: #f4f4f5;
}

.breadcrumb-current i {
  font-size: 14px;
  color: #6b7280;
}

[data-theme="dark"] .breadcrumb-current i {
  color: #9ca3af;
}

.region-dropdown-content::-webkit-scrollbar {
  width: 6px;
}

.region-dropdown-content::-webkit-scrollbar-track {
  background: transparent;
}

.region-dropdown-content::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

[data-theme="dark"] .region-dropdown-content::-webkit-scrollbar-thumb {
  background: #52525b;
}

.region-dropdown-loading,
.region-dropdown-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  gap: 12px;
  color: #6b7280;
}

[data-theme="dark"] .region-dropdown-loading,
[data-theme="dark"] .region-dropdown-empty {
  color: #9ca3af;
}

.region-dropdown-loading i,
.region-dropdown-empty i {
  font-size: 32px;
  opacity: 0.5;
}

.region-dropdown-loading span,
.region-dropdown-empty span {
  font-size: 14px;
  font-weight: 500;
  text-align: center;
}

.region-dropdown-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 4px;
  position: relative;
}

.region-dropdown-item:hover {
  background: #f1f5f9;
  transform: translateX(4px);
}

[data-theme="dark"] .region-dropdown-item:hover {
  background: #16161a;
}

.region-dropdown-item.active {
  background: linear-gradient(135deg, #eff6ff, #dbeafe);
  border-left: 3px solid #3b82f6;
  padding-left: 13px;
}

[data-theme="dark"] .region-dropdown-item.active {
  background: linear-gradient(135deg, #1e3a8a, #1e40af);
}

.region-dropdown-item i {
  font-size: 16px;
  color: #6b7280;
  flex-shrink: 0;
}

[data-theme="dark"] .region-dropdown-item i {
  color: #9ca3af;
}

.region-dropdown-item.active i {
  color: #3b82f6;
}

[data-theme="dark"] .region-dropdown-item.active i {
  color: #60a5fa;
}

.region-dropdown-item span {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

[data-theme="dark"] .region-dropdown-item span {
  color: #d1d5db;
}

.region-dropdown-item.active span {
  color: #1e40af;
  font-weight: 600;
}

[data-theme="dark"] .region-dropdown-item.active span {
  color: #93c5fd;
}

.region-dropdown-item .fa-chevron-right {
  margin-left: auto;
  font-size: 12px;
  color: #9ca3af;
  transition: all 0.2s ease;
}

.region-dropdown-item:hover .fa-chevron-right {
  color: #3b82f6;
  transform: translateX(2px);
}

[data-theme="dark"] .region-dropdown-item:hover .fa-chevron-right {
  color: #60a5fa;
}

.region-check {
  color: #3b82f6 !important;
  font-size: 14px;
  animation: checkPulse 0.3s ease-out;
  margin-left: auto;
}

[data-theme="dark"] .region-check {
  color: #60a5fa !important;
}

@keyframes checkPulse {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Animação Slide Up */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-up-enter-from {
  opacity: 0;
  transform: translateX(-50%) translateY(20px);
}

.slide-up-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(20px);
}

.slide-up-enter-to,
.slide-up-leave-from {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

/* Mobile layout adjustments */
@media (max-width: 768px) {
  /* Ajustes específicos do header mobile já definidos acima */
}

/* Garantir que o dropdown apareça corretamente */
.user-dropdown .dropdown-menu {
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
  position: absolute !important;
  z-index: 10000 !important;
}

/* Remover estilos antigos que não são mais necessários */
label {
  display: none;
}

a {
  color: inherit;
  text-decoration: none;
}
</style>
